(function () {
  const DEFAULT_FACTOR = 0.000835;

  class SkopeFundingWidget extends HTMLElement {
    static get observedAttributes() {
      return [
        "price",
        "price-cents",
        "daily-factor",
        "quote-url",
        "accent",
        "show-input",
        "currency",
        "logo-url",
        "product-title",
      ];
    }

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
      this.render();
    }

    attributeChangedCallback() {
      if (this.isConnected) this.render();
    }

    get price() {
      if (this.hasAttribute("price-cents")) {
        return Math.max(0, Number(this.getAttribute("price-cents")) || 0) / 100;
      }
      return Math.max(0, Number(this.getAttribute("price")) || 0);
    }

    set price(value) {
      this.removeAttribute("price-cents");
      this.setAttribute("price", String(value));
    }

    setPriceCents(value) {
      this.removeAttribute("price");
      this.setAttribute("price-cents", String(value));
    }

    get dailyFactor() {
      const value = Number(this.getAttribute("daily-factor"));
      return Number.isFinite(value) && value > 0 ? value : DEFAULT_FACTOR;
    }

    get showInput() {
      return this.hasAttribute("show-input") && this.getAttribute("show-input") !== "false";
    }

    calculate(price = this.price) {
      return Math.round(Math.max(0, Number(price) || 0) * this.dailyFactor * 100) / 100;
    }

    get productTitle() {
      const suppliedTitle = this.getAttribute("product-title");
      if (suppliedTitle) return suppliedTitle;

      return (
        document.querySelector('meta[property="og:title"]')?.content ||
        document.querySelector("main h1")?.textContent ||
        document.querySelector("h1")?.textContent ||
        document.title ||
        ""
      );
    }

    isEligibleProduct() {
      return /\b(skope|itv|irinox|vitamix)\b/i.test(this.productTitle);
    }

    formatMoney(value) {
      const currency = this.getAttribute("currency") || "NZD";
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 2,
      }).format(value);
    }

    render() {
      const eligible = this.isEligibleProduct();
      this.hidden = !eligible;
      if (!eligible) {
        this.shadowRoot.innerHTML = "";
        return;
      }

      const accent = this.getAttribute("accent") || "#e51c28";
      const quoteUrl = this.getAttribute("quote-url") || "";
      const logoUrl =
        this.getAttribute("logo-url") ||
        "https://cdn.shopify.com/s/files/1/0621/2209/8848/files/SKOPE-Simply-Hospitality.svg?v=1736233643";
      const rate = this.formatMoney(this.calculate());

      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            width: 100%;
            max-width: 300px;
            color: #162929;
            font-family: Arial, Helvetica, sans-serif;
          }
          * { box-sizing: border-box; }
          .panel {
            display: flex;
            min-height: 125px;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 9px 12px 7px;
            background: transparent;
            text-align: center;
          }
          .topline {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            width: 100%;
          }
          .panel-link {
            display: block;
            color: inherit;
            text-decoration: none;
          }
          .logo {
            display: block;
            width: 110px;
            max-height: 50px;
            margin: 0;
            object-fit: contain;
          }
          .headline {
            margin: 0;
            flex: 1;
            font-size: 15px;
            font-weight: 500;
            line-height: 1.3;
            text-align: left;
          }
          .rate { color: ${accent}; font-weight: 700; white-space: nowrap; }
          .included {
            margin: 7px 0 0;
            color: #354545;
            font-size: 10px;
            font-weight: 600;
            line-height: 1.3;
            white-space: nowrap;
          }
          .included::before {
            content: "\\2713";
            margin-right: 5px;
            color: ${accent};
            font-weight: 800;
          }
          .input-row { margin: 10px 0 0; width: 100%; }
          label {
            display: block;
            margin-bottom: 5px;
            color: #555f68;
            font-size: 12px;
            font-weight: 700;
          }
          input {
            width: 100%;
            border: 1px solid #aeb5bc;
            border-radius: 5px;
            padding: 9px 11px;
            color: #20252a;
            background: #fff;
            font: inherit;
          }
          input:focus { outline: 3px solid color-mix(in srgb, ${accent} 20%, transparent); border-color: ${accent}; }
          .cta {
            display: block;
            width: 100%;
            min-height: 39px;
            padding: 8px 0;
            background: #e51c28;
            color: #fff;
            border-radius: 4px;
            text-align: center;
            font-family: Figtree, sans-serif;
            font-size: 16px;
            font-weight: 400;
            line-height: 23px;
            text-decoration: none;
            text-underline-offset: 2px;
          }
          .fine {
            margin: 3px 0 0;
            color: #7a838b;
            font-size: 8px;
            line-height: 1.2;
          }
        </style>
        <div>
          ${quoteUrl ? `<a class="panel-link" href="${this.escapeAttribute(quoteUrl)}">` : ""}
          <section class="panel" aria-label="SKOPE funding estimate">
            <div class="topline">
              <img class="logo" src="${this.escapeAttribute(logoUrl)}" alt="SKOPE">
              <p class="headline">SKOPE Funding from just <span class="rate">${rate}/day</span></p>
            </div>
            <p class="included">Planned Maintenance and Extended Warranty included</p>
            ${
              this.showInput
                ? `<div class="input-row">
                    <label for="skope-price">Equipment price including GST</label>
                    <input id="skope-price" type="number" min="0" step="0.01" value="${this.price}">
                  </div>`
                : ""
            }
            <p class="fine">Indicative only. Subject to approval and final terms.</p>
          </section>
          ${quoteUrl ? `</a>` : ""}
          ${quoteUrl ? `<a class="cta" href="${this.escapeAttribute(quoteUrl)}">Apply to Funding</a>` : ""}
        </div>
      `;

      const input = this.shadowRoot.querySelector("input");
      if (input) {
        input.addEventListener("input", () => {
          this.price = input.value;
          this.dispatchEvent(
            new CustomEvent("skope-price-change", {
              detail: { price: this.price, estimatedDaily: this.calculate() },
              bubbles: true,
            })
          );
        });
      }
    }

    escapeAttribute(value) {
      return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll('"', "&quot;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");
    }
  }

  if (!customElements.get("skope-funding-widget")) {
    customElements.define("skope-funding-widget", SkopeFundingWidget);
  }
})();
